/**
 * FairwayBid – "So funktioniert es" als zwei Geschichten
 *
 * Eine Aufzählung erklärt, was das Portal tut. Eine Geschichte zeigt, warum
 * jemand es braucht – und das ist hier die Aufgabe. Deshalb zwei reale
 * Vorgänge, einer je Seite des Marktplatzes, beide auf dieselbe Beschaffung
 * bezogen: Derselbe kaputte Ballautomat, einmal aus Sicht der Golfanlage,
 * einmal aus Sicht des Händlers. Wer beide liest, versteht, warum die
 * Plattform beide Seiten braucht.
 *
 * Jede Geschichte hat eine Szene "Der alte Weg". Die ist der Punkt, an dem
 * es klickt: Nicht was FairwayBid kann, überzeugt, sondern was ohne
 * FairwayBid passiert.
 *
 * Die Namen sind Beispiele und als solche gekennzeichnet – erfundene
 * Kundenstimmen wären etwas anderes und kämen hier nicht hinein.
 */

export const HOWTO_PAGE_ID = 'so-funktioniert-es';
export const HOWTO_TYPE = 'fwb-schulung';

export const STORIES = [
  {
    key: 'club',
    tab: 'Eine Golfanlage',
    who:
      'Beispiel: Der Clubmanager einer 18-Loch-Anlage. Der Ballautomat an der Driving Range gibt keine Bälle mehr aus – mitten in der Saison.',
    scenes: [
      {
        kind: 'situation',
        time: 'Montag, 8:40 Uhr',
        title: 'Der Automat steht',
        text:
          'Die Range läuft, die Mitglieder stehen davor, der Automat gibt nichts mehr aus. Ersatz muss her – und eine Entscheidung, die der Vorstand mitträgt.',
      },
      {
        kind: 'old',
        time: 'So lief das bisher',
        title: 'Drei Hersteller, sieben Telefonate',
        text:
          'Googeln, anrufen, zweimal auf Rückruf warten. Ein Angebot kommt als PDF, eins als E-Mail im Fließtext, das dritte gar nicht. Montage im einen enthalten, im anderen nicht. Wer günstiger ist, weiß niemand.',
      },
      {
        kind: 'step',
        time: 'Montag, 8:44 Uhr',
        title: 'Bedarf einstellen – vier Minuten',
        text:
          'Kategorie „Ballautomaten" wählen. Die Vorlage füllt Titel und Beschreibung vor. Menge, Wunschtermin, Budgetrahmen, Frist auf 14 Tage. Absenden.',
      },
      {
        kind: 'step',
        time: 'Dienstag, 9:15 Uhr',
        title: 'Drei Angebote liegen da',
        text:
          'Alle drei mit Gesamtpreis, Lieferzeit, Garantie, Montage, Wartung pro Jahr und Rücknahme des Altgeräts. Nicht drei Anhänge – drei Zeilen in derselben Tabelle.',
      },
      {
        kind: 'step',
        time: 'Donnerstag, Vorstandssitzung',
        title: 'Zehn Minuten statt eine Stunde',
        text:
          'Eine Übersicht auf dem Beamer, das günstigste Angebot markiert. Die Diskussion dreht sich um Garantie und Liefertermin – nicht darum, welches Angebot was enthält.',
      },
      {
        kind: 'result',
        time: 'Ergebnis',
        title: 'Entschieden, dokumentiert, abgelegt',
        text:
          'Zuschlag erteilt, Vergabevermerk als Dokument gezogen: alle Angebote, die Kriterien, die Entscheidung, das Datum. Für Vorstand, Kämmerer und Prüfung. Kosten für die Anlage: keine.',
      },
    ],
  },
  {
    key: 'dealer',
    tab: 'Ein Händler',
    who:
      'Beispiel: Ein Händler für Range-Technik mit vier Außendienstlern. Liefert Ballautomaten, Matten und Netze in Deutschland und Österreich.',
    scenes: [
      {
        kind: 'situation',
        time: 'Dienstag, 9:00 Uhr',
        title: 'Der Vertrieb telefoniert',
        text:
          'Vier Leute rufen Golfanlagen an, um herauszufinden, ob gerade jemand etwas braucht. Neun von zehn brauchen nichts. Der zehnte hat vorgestern woanders gekauft.',
      },
      {
        kind: 'old',
        time: 'So lief das bisher',
        title: 'Kaltakquise auf Verdacht',
        text:
          'Kataloge verschicken, nachfassen, hoffen, dass man zufällig im richtigen Moment anruft. Die Anlage, deren Automat gerade ausgefallen ist, kennt man nicht – und erfährt es erst, wenn schon bestellt wurde.',
      },
      {
        kind: 'step',
        time: 'Einmalig',
        title: 'Profil einrichten – zwei Minuten',
        text:
          'Kategorien und Lieferländer hinterlegen: Ballautomaten, Matten, Netze – Deutschland und Österreich. Danach nie wieder anfassen.',
      },
      {
        kind: 'step',
        time: 'Montag, 8:45 Uhr',
        title: 'Die Anfrage kommt zu Ihnen',
        text:
          'Ballautomat, Budgetrahmen 15.000 bis 50.000 €, Frist 14 Tage, Lieferung nach Deutschland. Passt genau ins Sortiment. Niemand musste dafür angerufen werden.',
      },
      {
        kind: 'step',
        time: 'Montag, 11:20 Uhr',
        title: 'Angebot abgeben – fünf Minuten',
        text:
          'Preis, Lieferzeit, Garantie, Montage, Wartung, Rücknahme eintragen und absenden. Das feste Raster macht Ihr Angebot vergleichbar – wer sauber kalkuliert, gewinnt hier gegen den, der nur billig ist.',
      },
      {
        kind: 'result',
        time: 'Ergebnis',
        title: 'Im Rennen, ohne einen Kaltanruf',
        text:
          'Sie stehen im Vergleich bei einer Anlage, die kaufen will – nicht bei einer, die vielleicht irgendwann. Bei Zuschlag bekommen Sie die Kontaktdaten; Lieferung und Rechnung laufen direkt.',
      },
    ],
  },
];

/** Der gemeinsame Schluss unter beiden Geschichten. */
export const UNIQUE_POINTS = [
  {
    headline: 'Der Marktplatz läuft rückwärts',
    text:
      'Anderswo stellt der Händler ein Produkt ein und wartet. Hier beschreibt die Golfanlage ihren Bedarf – und die Anbieter kommen zu ihr. Deshalb steht am Anfang immer jemand, der wirklich kaufen will.',
  },
  {
    headline: 'Ein Raster für alle Angebote',
    text:
      'Preis, Lieferzeit, Garantie, Montage, Wartung, Rücknahme – jedes Angebot beantwortet dieselben Felder. Deshalb ist der Vergleich ein Vergleich und keine Auslegungssache.',
  },
  {
    headline: 'Angebote statt Anrufe',
    text:
      'Kontaktdaten werden erst nach der Annahme freigegeben. Wer eine Anfrage einstellt, bekommt Angebote ins Portal – und nicht sieben Anbieter ans Telefon.',
  },
  {
    headline: 'Die Entscheidung ist belegt',
    text:
      'Der Vergabevermerk fasst zusammen, wer was geboten hat und warum entschieden wurde. Für Vereine und kommunal getragene Anlagen ist das der Unterschied zwischen entscheiden und vertagen.',
  },
];
